import React from 'react';
import { Box, Stack, TextField, IconButton, Button, Typography } from '@mui/material';
import { Trash2, Link as LinkIcon, MessageSquare } from 'lucide-react';

// Compact buttons + links editor for stateless auto-replies (greeting / away /
// keyword replies). Mirrors the flow builder's button rules but without routing —
// these replies don't remember state.
//   • Reply buttons (max 3): tapping sends the label back, which can match another
//     keyword rule (handy for menus).
//   • Links (max 5): the first is a styled tap button; extras appear as tappable
//     text links at the bottom of the message.
// WhatsApp can't mix reply buttons and a link button in one message, so the two
// add-actions are mutually exclusive (with a note if a saved reply somehow has both).
export const MAX_REPLY = 3;
export const MAX_LINKS = 5;

// Stable local id for a freshly-created row, so React keys don't shift when a
// middle row is deleted (which previously caused input focus/value to jump).
let rowSeq = 0;
const newRowId = () => `row-${Date.now()}-${rowSeq++}`;

const WaReplyButtonsEditor = ({ buttons = [], onChange }) => {
  const list = Array.isArray(buttons) ? buttons : [];
  const replies = list.filter((b) => b.type !== 'url');
  const links = list.filter((b) => b.type === 'url');
  const hasReplies = replies.length > 0;
  const hasLinks = links.length > 0;

  const patch = (idx, p) => onChange(list.map((b, i) => (i === idx ? { ...b, ...p } : b)));
  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));
  const addReply = () => { if (replies.length >= MAX_REPLY) return; onChange([...list, { type: 'reply', id: newRowId(), title: '' }]); };
  const addLink = () => { if (links.length >= MAX_LINKS) return; onChange([...list, { type: 'url', id: newRowId(), title: '', url: '' }]); };

  // A stable key per row: prefer the row's own id; fall back to a composite that
  // is still better than a bare index for rows loaded from saved data.
  const rowKey = (b, i) => b.id || `${b.type}-${i}`;

  const firstLinkIdx = list.findIndex((b) => b.type === 'url');

  return (
    <Box sx={{ mt: 1 }}>
      {hasLinks && (
        <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 0.75 }}>
          The first link becomes a tap button; any extras are added as tappable links at the bottom of the message.
        </Typography>
      )}
      <Stack spacing={1}>
        {list.map((b, i) => (
          b.type === 'url' ? (
            <Stack key={rowKey(b, i)} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <TextField size="small" label={i === firstLinkIdx ? 'Link label (tap button)' : 'Link label'}
                inputProps={{ maxLength: i === firstLinkIdx ? 20 : 40 }}
                value={b.title || ''} onChange={(e) => patch(i, { title: e.target.value })}
                sx={{ width: { xs: '100%', sm: 200 } }} />
              <TextField size="small" label="Link URL" placeholder="https://…"
                value={b.url || ''} onChange={(e) => patch(i, { url: e.target.value })} sx={{ flex: 1 }} />
              <IconButton size="small" sx={{ color: 'error.main' }} onClick={() => remove(i)} aria-label="Delete link">
                <Trash2 size={14} />
              </IconButton>
            </Stack>
          ) : (
            <Stack key={rowKey(b, i)} direction="row" spacing={1} alignItems="flex-start">
              <TextField size="small" label="Button label" inputProps={{ maxLength: 20 }}
                helperText={`${(b.title || '').length}/20`}
                value={b.title || ''} onChange={(e) => patch(i, { title: e.target.value })} sx={{ flex: 1 }} />
              <IconButton size="small" sx={{ color: 'error.main', mt: 0.5 }} onClick={() => remove(i)} aria-label="Delete button">
                <Trash2 size={14} />
              </IconButton>
            </Stack>
          )
        ))}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mt: list.length ? 1 : 0 }}>
        {!hasLinks && replies.length < MAX_REPLY && (
          <Button size="small" startIcon={<MessageSquare size={14} />} onClick={addReply}>Add button</Button>
        )}
        {!hasReplies && links.length < MAX_LINKS && (
          <Button size="small" startIcon={<LinkIcon size={14} />} onClick={addLink}>
            {hasLinks ? 'Add another link' : 'Add link'}
          </Button>
        )}
      </Stack>

      {hasReplies && hasLinks && (
        <Typography sx={{ fontSize: 11, color: 'warning.main', mt: 0.75 }}>
          WhatsApp can't show reply buttons and a link button together — the links will be sent as text instead.
        </Typography>
      )}
    </Box>
  );
};

export default WaReplyButtonsEditor;
